/**
 * Icon resolver for backend-defined recommendation agents.
 *
 * The backend `AGENT_REGISTRY` (server/recommendations/pipeline/registry.py)
 * declares each agent's icon as a Lucide-react component name (string). The
 * frontend resolves that string to the actual component here, in one place,
 * so adding a new agent server-side requires adding its icon component to
 * the map below — and only here.
 *
 * Falls back to `Sparkles` for unknown icon names. This keeps the UI from
 * crashing if a backend change ships ahead of a frontend deploy, while
 * still surfacing the mismatch visibly.
 */

import {
  ArrowRight,
  Bell,
  CalendarClock,
  Coffee,
  Mail,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'

const AGENT_ICON_MAP: Record<string, LucideIcon> = {
  Mail,
  Coffee,
  ArrowRight,
  CalendarClock,
  Bell,
}

/**
 * Resolve a Lucide icon component for a backend-supplied agent icon name.
 *
 * @param iconName - Icon name from the server's AgentConfig.icon field
 * @returns The matching Lucide component, or Sparkles as a visible fallback
 */
export function resolveAgentIcon(iconName: string): LucideIcon {
  return AGENT_ICON_MAP[iconName] ?? Sparkles
}
